import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users,
  Radio,
  Bell,
  BellRing,
  ArrowUpRight,
  Sparkles,
  Mic,
  Hammer,
  Calendar,
  AlertTriangle,
  Activity,
  FileText,
  Wallet,
  Receipt,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatCurrencyBE } from "@/lib/currency";
import AiReliabilityWidget from "@/components/dashboard/AiReliabilityWidget";
import PaymentDelayWidget from "@/components/dashboard/PaymentDelayWidget";
import LossReasonsWidget from "@/components/dashboard/LossReasonsWidget";
import TasksTodayWidget from "@/components/dashboard/TasksTodayWidget";
import RdvSyncBadge from "@/components/rdv/RdvSyncBadge";
import { formatDateAndTimeSlot } from "@/utils/formatRdvTimeSlot";

const LEAD_STATUS_LABELS = {
  en_attente: "en attente",
  contacte: "contactés",
  rdv_fixe: "RDV fixés",
  devis_envoye: "devis envoyés",
  signe: "signés",
  perdu: "perdus",
};

export default function DashboardPage() {
  const { state } = useAuth();
  const [leadStats, setLeadStats] = useState({ total: 0, by_status: {} });
  const [chantiersActifs, setChantiersActifs] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [activeSources, setActiveSources] = useState(0);
  const [upcomingRdv, setUpcomingRdv] = useState([]);
  const [staleLeads, setStaleLeads] = useState({ total: 0, amber: 0, red: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingQuotes, setPendingQuotes] = useState({ count: 0, total_tvac: 0, oldest: [] });
  const [pendingInvoices, setPendingInvoices] = useState({ count: 0, total_tvac: 0, items: [] });
  // Phase γ-2 (12/09/2026) — Widget "Factures à envoyer" (a_envoyer seulement)
  // Réutilise l'endpoint γ-1 GET /api/dashboard/invoices-to-send-count (RBAC
  // par rôle : admin/secretaire = tenant, commercial = self, gestionnaire = 403).
  const [invoicesToSend, setInvoicesToSend] = useState(null);
  // Phase γ-3 (12/09/2026) — Bloc "Devis signés" avec indicateur acompte envoyé.
  // RBAC : admin/secretaire = tenant, commercial = self, gestionnaire = null (403 → bloc caché).
  const [signedQuotes, setSignedQuotes] = useState(null);
  // Phase B2 — Empty state widgets IA : décide si on masque AiReliabilityWidget
  // (dictations=0) pour ne pas afficher un widget zéros bruts. Fetch mutualisé
  // avec le widget lui-même (endpoint léger, dashboard-only).
  const [hasDictations, setHasDictations] = useState(null);  // null=loading, false=masqué, true=affiché

  useEffect(() => {
    // Phase B2 — Décide si AiReliabilityWidget doit être rendu (dictations>0)
    // Lot Accès commercial — endpoint restreint admin/secrétaire : les commerciaux
    // ne déclenchent PAS la requête (évite un 403 log parasite).
    if (hasRole(state?.user, ["commercial"])) {
      setHasDictations(false);
      return;
    }
    api.get("/dashboard/ai-reliability?period=30d")
      .then((r) => setHasDictations((r.data?.dictations?.total || 0) > 0))
      .catch(() => setHasDictations(false));
  }, [state?.user?.role]);

  useEffect(() => {
    const load = async () => {
      try {
        const nowIso = new Date().toISOString();
        const in7d = new Date(Date.now() + 7 * 86400000).toISOString();
        const [leadsRes, chantiersRes, notifsRes, sourcesRes, rdvRes, quotesRes, invoicesRes] = await Promise.all([
          api.get("/leads?page_size=500"),
          api.get("/chantiers"),
          api.get("/notifications?only_unread=true"),
          api.get("/lead-sources"),
          api.get(`/rendezvous?from_=${nowIso}&to=${in7d}`).catch(() => ({ data: [] })),
          api.get("/quotes?status=envoye").catch(() => ({ data: { items: [] } })),
          // Lot Accès commercial — endpoint restreint admin/secrétaire :
          // les commerciaux reçoivent un stub vide (widget masqué de toute façon).
          // Phase B rôle gestionnaire_chantier (2026-09-10) — Même stub (backend
          // renvoie 403 pour gestionnaire_chantier car il n'est pas dans le set admin+secretaire).
          hasRole(state?.user, ["commercial", "gestionnaire_chantier"])
            ? Promise.resolve({ data: { count: 0, total_tvac: 0, items: [] } })
            : api.get("/dashboard/pending-invoices").catch(() => ({ data: { count: 0, total_tvac: 0, items: [] } })),
        ]);
        // Phase γ-2 — Widget factures à envoyer (RBAC : gestionnaire_chantier
        // reçoit 403 backend → on stub à null, widget masqué).
        if (!hasRole(state?.user, ["gestionnaire_chantier"])) {
          api.get("/dashboard/invoices-to-send-count")
            .then((r) => setInvoicesToSend(r.data || null))
            .catch(() => setInvoicesToSend(null));
          // Phase γ-3 — Bloc Devis signés (même RBAC — gestionnaire 403)
          api.get("/dashboard/signed-quotes")
            .then((r) => setSignedQuotes(r.data || null))
            .catch(() => setSignedQuotes(null));
        }
        const leads = leadsRes.data.items || [];
        const openLeads = leads.filter((l) => l.status !== "perdu" && !l.deleted_at);
        const by_status = openLeads.reduce((acc, l) => {
          acc[l.status] = (acc[l.status] || 0) + 1;
          return acc;
        }, {});
        setLeadStats({ total: openLeads.length, by_status });

        // Stale leads : en_attente ≥ 3 jours
        const nowMs = Date.now();
        const stale = leads
          .filter((l) => l.status === "en_attente")
          .map((l) => {
            const ref = l.last_activity_at || l.created_at;
            const days = Math.floor((nowMs - new Date(ref).getTime()) / 86400000);
            return { ...l, days };
          })
          .filter((l) => l.days >= 3);
        setStaleLeads({
          total: stale.length,
          amber: stale.filter((l) => l.days < 7).length,
          red: stale.filter((l) => l.days >= 7).length,
        });

        const chantiers = chantiersRes.data.items || chantiersRes.data || [];
        setChantiersActifs(chantiers.filter((c) => c.status && c.status !== "termine").length);
        setUnreadNotifs(notifsRes.data.unread_count || 0);
        setActiveSources((sourcesRes.data || []).filter((s) => s.active).length);
        setUpcomingRdv(rdvRes.data.slice(0, 5));

        // Widget Phase 3a2.2b : devis envoyés en attente de signature
        const pendingItems = (quotesRes.data.items || []).slice();
        const totalTvac = pendingItems.reduce((s, q) => s + (Number(q.total_tvac) || 0), 0);
        pendingItems.sort((a, b) => new Date(a.sent_at || a.created_at) - new Date(b.sent_at || b.created_at));
        setPendingQuotes({
          count: pendingItems.length,
          total_tvac: totalTvac,
          oldest: pendingItems.slice(0, 5),
        });
        setPendingInvoices(invoicesRes.data || { count: 0, total_tvac: 0, items: [] });

        // Activité récente : agrège les interactions récentes de tous les leads (top 15 par lead récent)
        const recent = [];
        const recentLeads = leads.slice(0, 15);
        for (const l of recentLeads) {
          try {
            const detail = await api.get(`/leads/${l.id}`);
            for (const i of detail.data.interactions || []) {
              recent.push({ ...i, lead_id: l.id, lead_name: `${l.first_name} ${l.last_name}`.trim() });
            }
          } catch {
            /* skip */
          }
        }
        recent.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setRecentActivity(recent.slice(0, 10));
      } catch {
        /* silencieux — dashboard tolérant */
      }
    };
    load();
    // Bug #2 fix (14/09/2026) — refetch quand company change dans le même onglet.
  }, [state?.company?.id]);

  if (!state) return null;
  const { user, company } = state;
  const isCommercial = hasRole(user, ["commercial"]);
  // Phase B rôle gestionnaire_chantier — Widgets "impayés client" / "loss reasons"
  // / "AI reliability" masqués : le gestionnaire n'a pas d'accès pipeline commercial
  // et pas de visibilité impayés client (matrice Phase B).
  const isGestionnaireChantier = hasRole(user, ["gestionnaire_chantier"]);
  const hidePendingInvoices = isCommercial || isGestionnaireChantier;

  const leadBreakdown = Object.entries(leadStats.by_status)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${LEAD_STATUS_LABELS[k] || k}`)
    .join(" · ");

  const stats = [
    {
      key: "leads",
      label: "Leads actifs",
      value: leadStats.total,
      icon: Users,
      caption: leadBreakdown || "Aucun lead ouvert",
      to: "/leads",
    },
    {
      key: "chantiers",
      label: "Chantiers en cours",
      value: chantiersActifs,
      icon: Hammer,
      caption:
        chantiersActifs === 0 ? "Aucun chantier ouvert" : "Suivi opérationnel",
      to: "/leads",
    },
    {
      key: "notifs",
      label: "Notifications",
      value: unreadNotifs,
      icon: unreadNotifs > 0 ? BellRing : Bell,
      caption: unreadNotifs === 0 ? "Rien de neuf" : "Non lues",
      to: null,
    },
    {
      key: "sources",
      label: "Sources actives",
      value: activeSources,
      icon: Radio,
      caption: "Provenances de leads",
      to: "/settings/lead-sources",
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <p className="text-sm text-[#C4553B]">Bonjour {user.first_name} 👷</p>
        <h1
          className="mt-1 text-3xl font-bold sm:text-4xl"
          data-testid="dashboard-heading"
        >
          Aperçu de votre activité
        </h1>
        <p className="mt-2 text-sm text-slate-400">{company.name}</p>
      </header>

      <TasksTodayWidget />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((s) => {
          const Inner = (
            <Card
              className="h-full border-slate-800 bg-slate-900/40 transition-colors hover:border-[#C4553B]/40"
              data-testid={`stat-${s.key}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <s.icon className="h-4 w-4 text-[#C4553B]" />
                </div>
                <div className="mt-3 text-2xl font-semibold text-slate-100 tabular-nums">
                  {s.value}
                </div>
                <div className="text-xs font-medium text-slate-300">
                  {s.label}
                </div>
                <div className="mt-1 line-clamp-2 min-h-[2rem] text-[11px] text-slate-500">
                  {s.caption}
                </div>
              </CardContent>
            </Card>
          );
          return s.to ? (
            <Link key={s.key} to={s.to} className="block">
              {Inner}
            </Link>
          ) : (
            <div key={s.key}>{Inner}</div>
          );
        })}
      </div>

      <Card className="border-[#C4553B]/30 bg-gradient-to-br from-slate-900/70 to-slate-900/30">
        <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-[#C4553B]">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">
                Fonctionnalité clé
              </span>
            </div>
            <h2 className="mt-2 text-xl font-bold text-slate-100">
              Devis dicté par IA
            </h2>
            <p className="mt-1 max-w-xl text-sm text-slate-400">
              Créez un brouillon de devis en dictant vos observations sur
              chantier — transcription et structuration automatiques.
            </p>
          </div>
          <Link
            to="/poc"
            className="inline-flex items-center gap-2 self-start rounded-full bg-[#C4553B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#A8402C] sm:self-auto"
            data-testid="dashboard-poc-link"
          >
            <Mic className="h-4 w-4" /> Ouvrir l&apos;assistant vocal
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        {isCommercial ? null : hasDictations ? (
          <>
            <div className="lg:col-span-2">
              <AiReliabilityWidget />
            </div>
            <PaymentDelayWidget />
          </>
        ) : (
          <div className="lg:col-span-3">
            <PaymentDelayWidget />
          </div>
        )}
      </div>

      {isCommercial ? null : <LossReasonsWidget />}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Phase γ-2 (12/09/2026) — Widget "Factures à envoyer" (a_envoyer only).
            Rendu conditionnel : masqué pour gestionnaire_chantier (invoicesToSend=null)
            + masqué si count=0 (widget conditionnel — doctrine "0 = pas de widget"). */}
        {invoicesToSend && invoicesToSend.count > 0 && (
          <Link
            to="/factures?status=a_envoyer"
            className="block"
            data-testid="widget-invoices-to-send"
          >
            <Card
              className={`h-full border-slate-800 bg-slate-900/40 transition-colors ${
                invoicesToSend.has_overdue_to_send
                  ? "hover:border-[#A63D2F]/60"
                  : "hover:border-[#C4553B]/40"
              }`}
            >
              <CardContent className="p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Receipt
                    className={`h-4 w-4 ${
                      invoicesToSend.has_overdue_to_send ? "text-[#A63D2F]" : "text-[#C4553B]"
                    }`}
                  />
                  <h3 className="text-sm font-semibold text-slate-200">Factures à envoyer</h3>
                  {invoicesToSend.has_overdue_to_send && (
                    <span
                      className="ml-auto rounded-full bg-[#A63D2F] px-2 py-0.5 text-[10px] font-bold text-white"
                      data-testid="widget-invoices-to-send-j3-badge"
                    >
                      J+3
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <span
                      className="text-3xl font-bold text-slate-100 tabular"
                      data-testid="widget-invoices-to-send-count"
                    >
                      {invoicesToSend.count}
                    </span>
                    <span
                      className="tabular text-sm text-slate-300"
                      data-testid="widget-invoices-to-send-total-tvac"
                    >
                      {formatCurrencyBE(invoicesToSend.total_tvac)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {invoicesToSend.count === 1 ? "facture" : "factures"} créée(s), en attente d'envoi au client
                    {invoicesToSend.has_overdue_to_send && (
                      <span className="text-[#E9A98F]">
                        {" · "}
                        {invoicesToSend.j3_count} depuis plus de 3 jours
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        <Link
          to="/quotes?status=envoye"
          className="block"
          data-testid="widget-pending-quotes"
        >
          <Card className="h-full border-slate-800 bg-slate-900/40 transition-colors hover:border-[#D99A2B]/40">
            <CardContent className="p-4">
              <div className="mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#D99A2B]" />
                <h3 className="text-sm font-semibold text-slate-200">Devis en attente de signature</h3>
              </div>
              {pendingQuotes.count === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-6 text-center text-xs text-slate-500">
                  <FileText className="h-6 w-6 text-slate-700" />
                  <span>Aucun devis en attente de signature</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <span className="text-3xl font-bold text-slate-100 tabular">{pendingQuotes.count}</span>
                    <span className="tabular text-sm text-slate-300" data-testid="pending-quotes-total-tvac">
                      {formatCurrencyBE(pendingQuotes.total_tvac)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {pendingQuotes.count === 1 ? "devis envoyé" : "devis envoyés"} · montant cumulé TVAC
                  </div>
                  {pendingQuotes.oldest.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {pendingQuotes.oldest.map((q) => {
                        const sent = new Date(q.sent_at || q.created_at);
                        const days = Math.max(0, Math.floor((Date.now() - sent.getTime()) / 86400000));
                        return (
                          <li key={q.id} className="flex items-center justify-between text-xs">
                            <span className="truncate text-slate-300 font-mono">
                              {q.number || "—"} · {q.client_display}
                            </span>
                            <span className="tabular text-slate-500">{days}j</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </Link>

        {hidePendingInvoices ? null : (
        <div className="block" data-testid="widget-unpaid-invoices">
          <Card className="h-full border-slate-800 bg-slate-900/40 transition-colors hover:border-[#C4553B]/40">
            <CardContent className="p-4">
              <div className="mb-3 flex items-center gap-2">
                <Wallet className="h-4 w-4 text-[#C4553B]" />
                <h3 className="text-sm font-semibold text-slate-200">Factures en attente de paiement</h3>
              </div>
              {pendingInvoices.count === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-6 text-center text-xs text-slate-500">
                  <Wallet className="h-6 w-6 text-slate-700" />
                  <span>Aucune facture en attente de paiement</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <span
                      className="text-3xl font-bold text-slate-100 tabular"
                      data-testid="widget-unpaid-invoices-count"
                    >
                      {pendingInvoices.count}
                    </span>
                    <span className="tabular text-sm text-slate-300" data-testid="unpaid-invoices-total-tvac">
                      {formatCurrencyBE(pendingInvoices.total_tvac)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {pendingInvoices.count === 1 ? "facture envoyée" : "factures envoyées"} · en attente de paiement client
                  </div>
                  {pendingInvoices.items.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {pendingInvoices.items.slice(0, 5).map((inv) => (
                        <li
                          key={inv.id}
                          className="flex items-center justify-between text-xs"
                          data-testid={`unpaid-invoice-${inv.id}`}
                        >
                          <Link
                            to={`/chantiers/${inv.chantier_id}`}
                            className="truncate text-slate-300 font-mono hover:text-[#C4553B]"
                          >
                            {inv.number} · {inv.client_display}
                          </Link>
                          <span className={`tabular ${inv.days_overdue > 0 ? "text-[#E9A98F]" : "text-slate-500"}`}>
                            {inv.days_overdue > 0 ? `${inv.days_overdue}j retard` : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        )}

        <Card className="border-slate-800 bg-slate-900/40" data-testid="widget-upcoming-rdv">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                <Calendar className="h-4 w-4 text-[#C4553B]" /> Rendez-vous cette semaine
              </h3>
              {upcomingRdv.length > 0 && (
                <Link to="/leads" className="text-xs text-[#C4553B] hover:underline">Tout voir</Link>
              )}
            </div>
            {upcomingRdv.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center gap-2 py-6 text-center text-xs text-slate-500"
                data-testid="upcoming-rdv-empty"
              >
                <Calendar className="h-6 w-6 text-slate-700" />
                <span>Aucun rendez-vous cette semaine</span>
              </div>
            ) : (
              <ul className="space-y-2">
                {upcomingRdv.map((r) => (
                  <li key={r.id} className="text-xs">
                    <Link
                      to={`/leads/${r.lead_id}`}
                      className="block rounded border border-slate-800 bg-slate-950/30 p-2 hover:border-[#C4553B]/40"
                    >
                      <div className="font-medium text-slate-100 flex items-center gap-1.5">
                        {formatDateAndTimeSlot(r.scheduled_at, r.duration_minutes)}
                        <RdvSyncBadge status={r.google_sync_status} />
                      </div>
                      <div className="mt-0.5 truncate text-slate-400">
                        {r.contact_name || "—"} · {r.location || "lieu à préciser"}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {staleLeads.total > 0 && (
          <Link to="/leads" className="block" data-testid="widget-stale-leads">
            <Card className="h-full border-slate-800 bg-slate-900/40 transition-colors hover:border-[#D99A2B]/40">
              <CardContent className="p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-200">
                  <AlertTriangle className="h-4 w-4 text-[#D99A2B]" /> Leads à relancer
                </h3>
                <div className="text-3xl font-bold text-slate-100 tabular-nums">{staleLeads.total}</div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                  {staleLeads.amber > 0 && (
                    <span className="rounded-full border border-[#D99A2B]/60 bg-[#D99A2B]/15 px-2 py-0.5 text-[#F0C572]">
                      {staleLeads.amber} entre 3 et 6 jours
                    </span>
                  )}
                  {staleLeads.red > 0 && (
                    <span className="rounded-full border border-[#A63D2F]/70 bg-[#A63D2F] px-2 py-0.5 text-white">
                      {staleLeads.red} ≥ 7 jours
                    </span>
                  )}
                </div>
                <p className="mt-2 text-[11px] text-slate-500">Cliquez pour ouvrir la vue leads.</p>
              </CardContent>
            </Card>
          </Link>
        )}

        {recentActivity.length > 0 && (
          <Card className="border-slate-800 bg-slate-900/40 lg:col-span-1" data-testid="widget-recent-activity">
            <CardContent className="p-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-200">
                <Activity className="h-4 w-4 text-[#C4553B]" /> Activité récente
              </h3>
              <ul className="space-y-2 text-xs">
                {recentActivity.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/leads/${a.lead_id}`}
                      className="block rounded border border-slate-800 bg-slate-950/30 p-2 hover:border-[#C4553B]/40"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                          {a.type.replace("_", " ")}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          {new Date(a.created_at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                      </div>
                      <div className="mt-1 font-medium text-slate-100 truncate">{a.lead_name || "Lead"}</div>
                      <div className="mt-0.5 truncate text-slate-400">{a.content || "—"}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Phase γ-3 (12/09/2026) — Bloc Dashboard "Devis signés" avec indicateur binaire
          "Acompte envoyé". Rendu conditionnel : masqué pour gestionnaire_chantier
          (endpoint 403 → state null). Rendu MÊME si count=0 (contrairement au widget γ-2)
          avec état vide clair. */}
      {signedQuotes && (
        <Card className="border-slate-800 bg-slate-900/40" data-testid="dashboard-block-signed-quotes">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                <FileText className="h-4 w-4 text-[#4CAF50]" /> Devis signés
              </h3>
              {signedQuotes.count > 0 && (
                <div className="flex items-baseline gap-3 text-xs">
                  <span
                    className="text-2xl font-bold text-slate-100 tabular"
                    data-testid="signed-quotes-count"
                  >
                    {signedQuotes.count}
                  </span>
                  <span
                    className="tabular text-slate-300"
                    data-testid="signed-quotes-total-tvac"
                  >
                    {formatCurrencyBE(signedQuotes.total_tvac)}
                  </span>
                </div>
              )}
            </div>
            {signedQuotes.count === 0 ? (
              <div
                className="flex flex-col items-center justify-center gap-2 py-6 text-center text-xs text-slate-500"
                data-testid="signed-quotes-empty"
              >
                <FileText className="h-6 w-6 text-slate-700" />
                <span>Aucun devis signé en attente de suivi facturation</span>
              </div>
            ) : (
              <ul className="space-y-1.5">
                {signedQuotes.quotes.slice(0, 10).map((q) => (
                  <li
                    key={q.id}
                    className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2 text-xs"
                    data-testid={`signed-quote-row-${q.id}`}
                  >
                    <Link
                      to={q.chantier_id ? `/chantiers/${q.chantier_id}` : `/quotes/${q.id}`}
                      className="flex flex-1 items-center gap-3 truncate text-slate-300 hover:text-[#C4553B]"
                    >
                      <span className="font-mono text-slate-200">{q.number}</span>
                      <span className="truncate text-slate-400">{q.client_name}</span>
                      <span className="tabular text-slate-300">{formatCurrencyBE(q.total_tvac)}</span>
                    </Link>
                    {q.acompte_all_sent ? (
                      <span
                        className="ml-3 flex items-center gap-1 rounded-full bg-[#4CAF50]/15 px-2 py-0.5 text-[11px] font-semibold text-[#4CAF50]"
                        data-testid={`signed-quote-acompte-sent-${q.id}`}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        Acompte envoyé
                      </span>
                    ) : (
                      <span
                        className="ml-3 flex items-center gap-1 rounded-full bg-[#A63D2F]/15 px-2 py-0.5 text-[11px] font-semibold text-[#E9A98F]"
                        data-testid={`signed-quote-acompte-not-sent-${q.id}`}
                      >
                        <XCircle className="h-3 w-3" />
                        Acompte non envoyé
                      </span>
                    )}
                  </li>
                ))}
                {signedQuotes.count > 10 && (
                  <li className="text-center text-[11px] text-slate-500">
                    …et {signedQuotes.count - 10} autre(s) devis signé(s)
                  </li>
                )}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
